import { FunctionComponent, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import Loading from "../../Loading/Loading";
import { createNewsSuperAdmin } from "../../../Services/organizations";
import { showSnackbarAction } from "../../../Redux/actions";
import TextInput from "../../TextInput/InputText";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";

type props = {
  /** Controls whether the modal is visible */
  open: boolean;

  /** Callback to close modal */
  close: () => void;

  /** Organization ID (Super Admin may create news globally) */
  organizationId: any;

  /** List of organization users (not used directly here but mapped for consistency) */
  organizationUsers: any;

  /** Theme: true = light, false = dark */
  theme?: any;
};

/**
 * NewsModal (Super Admin)
 * --------------------------------------------------------------
 * This modal allows a Super Admin to add a global "News Link" entry.
 * Fields:
 *   - Area Name
 *   - News Link URL
 *
 * Behavior:
 *   ✔ Form validation for required fields  
 *   ✔ Trigger `createNewsSuperAdmin` service on submit  
 *   ✔ Displays loader while saving  
 *   ✔ Uses `TextInput` and styled buttons  
 *   ✔ Respects theme and is mobile responsive  
 */
const NewsModal: FunctionComponent<props> = ({
  organizationId,
  open,
  close,
  theme
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** Refs for controlled input fields */
  const areaRef: any = useRef();
  const linkRef: any = useRef();

  /** Loader state for submit button */
  const [load, setLoad] = useState(false);

  const dispatcher = useDispatch();

  /**
   * Handle Save (Submit) action
   * ------------------------------------
   * 1. Validates both fields
   * 2. Shows error message via Snackbar
   * 3. Calls `createNewsSuperAdmin` to store data
   * 4. Closes modal on success
   */
  const onSave = () => {
    if (areaRef.current.value === "") {
      dispatcher(showSnackbarAction("Please Enter Area Name!!", "error"));
    } else if (linkRef.current.value === "") {
      dispatcher(showSnackbarAction("Please Enter News Link!!", "error"));
    } else {
      setLoad(true);
      createNewsSuperAdmin(
        areaRef.current.value,
        linkRef.current.value,
        dispatcher,
        (val) => setLoad(val),
        close
      );
    }
  };

    /**
   * Modal styling configuration for react-modal
   * Styled dynamically according to theme + mobile responsiveness
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };
  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
      {/* Loader overlay */}
      {load && <Loading />}
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <form style={{ width: "100%" }}>
           {/* Modal Heading */}
          <Box sx={{ mb: 2 }}>
            <h3
              style={{
                color: theme ? "#272944" : "#fff",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-large)",
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              Create News Link
            </h3>
          </Box>
            
            {/* FORM FIELDS */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            
             {/* Area Name */}
            <Grid item xs={12}>
              <TextInput
                title="Area Name"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={areaRef}
                required={true}
              />
            </Grid>
           
            {/* News Link */}
            <Grid item xs={12}>
              <TextInput
                title="Link"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={linkRef}
                required={true}
              />
            </Grid>
          </Grid>

           {/* FOOTER BUTTONS */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                width: "100%",
              }}
            >
               {/* Cancel Button */}
              <Box style={{ marginRight: 8 }}>
                <StyledCancelButton onClick={close} isMobile={isMobile}>
                  Cancel
                </StyledCancelButton>
              </Box>
              
               {/* Submit Button */}
              <Box>
                <StyledSubmitButton
                  onClick={onSave}
                  themeMode={themeMUI.palette.mode}
                  isMobile={isMobile}
                >
                  {load ? (
                    <span style={{ display: "flex", justifyContent: "center" }}>
                      <Circles color="#fff" height={20} width={20} />
                    </span>
                  ) : (
                    <>Submit</>
                  )}
                </StyledSubmitButton>
              </Box>
            </Box>
          </Grid>
        </form>
      </Box>
    </Modal >
  );
};

const mapStateToProps = (state: any) => {
  return {
    organizationId: state.organization.id,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(NewsModal);
